/**
 * TCR-2128 — helper thuần cho "Sổ địa chỉ" ở tab Thông tin giao nhận.
 *
 * Entry trả về từ `GET customers/:id/addresses` đi qua `toJSON` của
 * `customer-address.schema.ts`: `_id` → `address_id`, `city_id`/`ward_id` bị đổi thành `city`/`ward`
 * và là object populate — KHÔNG tin type `CustomerAddressItem` (khai báo string `city_id`/`ward_id`).
 * Vì vậy mọi chỗ đọc đều đi qua `readAddressLocationId`/`readAddressLocationName` dưới đây.
 *
 * `City` KHÔNG dùng `_id` làm khoá định danh trong response — toJSON riêng của nó đổi thành
 * `city_id` (khớp `location.service.ts: interface City { city_id: string }`), trong khi `Ward` vẫn
 * giữ `_id` như Mongoose mặc định. Chỉ đọc `v._id` (bỏ qua `city_id`) khiến delivery_city_id luôn
 * rỗng — form coi tab Giao nhận là "chưa điền đủ" và bỏ qua ghi sổ, im lặng không báo lỗi gì.
 */

import { isMaskedCccd } from './customerFormSchema';

export type AddressLocationRef =
  | string
  | { _id?: string; city_id?: string; ward_id?: string; name?: string }
  | null
  | undefined;

export function readAddressLocationId(v: AddressLocationRef): string {
  if (!v) return '';
  return typeof v === 'string' ? v : (v._id ?? v.city_id ?? v.ward_id ?? '');
}

export function readAddressLocationName(v: AddressLocationRef): string {
  if (!v || typeof v === 'string') return '';
  return v.name ?? '';
}

/** Hình dạng entry sổ địa chỉ như API thực sự trả về (đọc phòng thủ). */
export interface AddressBookEntry {
  address_id: string;
  name?: string;
  phone?: string;
  email?: string;
  citizen_id?: string;
  address?: string;
  is_default?: boolean;
  amis_account_object_id?: string;
  city?: AddressLocationRef;
  city_id?: AddressLocationRef;
  ward?: AddressLocationRef;
  ward_id?: AddressLocationRef;
  /** TCR-2384 */
  contact_code?: string;
  /** TCR-2384 — Tên địa chỉ giao hàng (tên điểm giao); `name` là người nhận chính. */
  address_name?: string;
  note?: string;
  receivers?: { name: string; phone: string; note?: string }[];
}

/** TCR-2384 — 1 người nhận của 1 địa chỉ; `is_main` = người chính (name/phone của chính entry). */
export interface DeliveryRecipient {
  name: string;
  phone: string;
  note?: string;
  is_main: boolean;
}

/** Người chính (nếu có tên) đứng đầu, sau đó là các người nhận thêm. */
export function entryRecipients(e: AddressBookEntry): DeliveryRecipient[] {
  const out: DeliveryRecipient[] = [];
  if (e.name) out.push({ name: e.name, phone: e.phone ?? '', is_main: true });
  for (const r of e.receivers ?? []) {
    out.push({ name: r.name, phone: r.phone, ...(r.note ? { note: r.note } : {}), is_main: false });
  }
  return out;
}

/** Độ dài tối đa ghi chú giao hàng tự điền (khớp giới hạn field ghi chú). */
export const DELIVERY_NOTE_AUTO_MAX = 500;

/**
 * Ghi chú giao hàng tự điền: "<ghi chú địa chỉ>. Liên hệ khác: Tên SĐT (ghi chú); …"
 * — chỉ gồm người KHÔNG được chọn (khác `chosenPhone`). Quá 500 ký tự thì bỏ bớt người cuối + " …".
 */
export function buildDeliveryNote(
  addressNote: string | undefined,
  recipients: DeliveryRecipient[],
  chosenPhone: string,
): string {
  const chosen = chosenPhone.trim();
  const items = recipients
    .filter((r) => r.phone.trim() !== chosen)
    .map((r) => {
      const base = [r.name, r.phone].map((x) => x.trim()).filter(Boolean).join(' ');
      const note = (r.note ?? '').trim();
      return note ? `${base} (${note})` : base;
    })
    .filter(Boolean);
  const head = (addressNote ?? '').trim();
  const join = (list: string[], ellipsis: boolean): string => {
    const others = list.length ? `Liên hệ khác: ${list.join('; ')}${ellipsis ? ' …' : ''}` : '';
    return [head, others].filter(Boolean).join('. ');
  };
  for (let k = items.length; k >= 0; k--) {
    const text = join(items.slice(0, k), k < items.length);
    if (text.length <= DELIVERY_NOTE_AUTO_MAX) return text;
  }
  return head.slice(0, DELIVERY_NOTE_AUTO_MAX);
}

/** Chuẩn hoá SĐT để so sánh: bỏ khoảng trắng/chấm/gạch, `+84`/`84` đầu → `0`. */
export function normalizePhone(phone: string | undefined | null): string {
  const p = (phone ?? '').replace(/[\s.\-]/g, '');
  if (p.startsWith('+84')) return `0${p.slice(3)}`;
  if (/^84\d{9}$/.test(p)) return `0${p.slice(2)}`;
  return p;
}

/** Tên + SĐT đang trên form có phải 1 trong các người nhận của địa chỉ nguồn không. */
export function matchRecipient(
  source: DeliveryAddressSource | undefined,
  name: string,
  phone: string,
): DeliveryRecipient | undefined {
  if (!source?.recipients) return undefined;
  const p = normalizePhone(phone);
  const n = normalize(name);
  return source.recipients.find((r) => normalizePhone(r.phone) === p && normalize(r.name) === n);
}

export function entryCity(e: AddressBookEntry): AddressLocationRef {
  return e.city_id ?? e.city;
}

export function entryWard(e: AddressBookEntry): AddressLocationRef {
  return e.ward_id ?? e.ward;
}

export function formatEntryAddress(e: AddressBookEntry): string {
  return [e.address, readAddressLocationName(entryWard(e)), readAddressLocationName(entryCity(e))]
    .filter(Boolean)
    .join(', ');
}

/** Lọc theo tên / SĐT / địa chỉ đầy đủ, không phân biệt hoa thường & dấu. */
export function filterAddressEntries(list: AddressBookEntry[], query: string): AddressBookEntry[] {
  const q = normalize(query);
  if (!q) return list;
  return list.filter((e) =>
    [
      e.address_name,
      e.name,
      e.phone,
      e.contact_code,
      formatEntryAddress(e),
      ...(e.receivers ?? []).flatMap((r) => [r.name, r.phone]),
    ].some((s) => normalize(s ?? '').includes(q)),
  );
}

function normalize(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase()
    .trim();
}

/** Các field giao nhận được so để biết người dùng có sửa tay hay không (KHÔNG gồm ghi chú). */
export const DELIVERY_SNAPSHOT_KEYS = [
  'delivery_name',
  'delivery_phone',
  'delivery_email',
  'delivery_citizen_id',
  'delivery_city_id',
  'delivery_ward_id',
  'delivery_address',
] as const;

export type DeliverySnapshot = Record<(typeof DELIVERY_SNAPSHOT_KEYS)[number], string>;

/**
 * Giá trị field ẩn `_deliveryAddressSource`: địa chỉ trên form đang xuất phát từ đâu.
 * `address_id` có ⇒ từ 1 entry trong sổ (lưu sẽ CẬP NHẬT entry đó); không có ⇒ thêm mới.
 */
export interface DeliveryAddressSource {
  address_id?: string;
  snapshot: DeliverySnapshot;
  /** TCR-2384: các người nhận của entry lúc áp (để nhận ra người đang chọn, không coi là sửa tay). */
  recipients?: DeliveryRecipient[];
  note?: string;
  /** TCR-2384 — tên điểm giao của entry; chỉ còn đúng khi địa chỉ chưa bị sửa tay (`deliveryAddressName`). */
  address_name?: string;
}

const ADDRESS_LOCATION_KEYS = ['delivery_city_id', 'delivery_ward_id', 'delivery_address'] as const;

/**
 * TCR-2384 — tên điểm giao của địa chỉ đang giao tới (lưu vào đơn, hiện ở POS). Chỉ trả khi địa chỉ
 * xuất phát từ 1 entry trong sổ và thành phố / phường / địa chỉ chi tiết còn y như entry: đổi người
 * nhận không sao, nhưng sửa tay sang chỗ khác thì tên cũ không còn đúng.
 */
export function deliveryAddressName(values: Record<string, unknown>): string {
  const source = values._deliveryAddressSource as DeliveryAddressSource | undefined;
  const name = source?.address_name?.trim();
  if (!source?.address_id || !name || values.delivery_same_as_customer) return '';
  const now = takeDeliverySnapshot(values);
  return ADDRESS_LOCATION_KEYS.every((k) => now[k] === source.snapshot[k]) ? name : '';
}

export function takeDeliverySnapshot(values: Record<string, unknown>): DeliverySnapshot {
  const out = {} as DeliverySnapshot;
  for (const k of DELIVERY_SNAPSHOT_KEYS) out[k] = String(values[k] ?? '').trim();
  return out;
}

/**
 * Người dùng đã sửa tay khối giao nhận chưa — điều kiện hiện ô "Lưu vào Sổ địa chỉ".
 * Chưa có nguồn (đơn mở lại, khách chưa từng đổ tự động) ⇒ coi như đã sửa tay nếu có dữ liệu.
 */
export function isDeliveryEdited(
  values: Record<string, unknown>,
  source: DeliveryAddressSource | undefined,
): boolean {
  const now = takeDeliverySnapshot(values);
  if (!source) return DELIVERY_SNAPSHOT_KEYS.some((k) => now[k] !== '');
  // Chọn người nhận khác của CÙNG địa chỉ không phải sửa tay: bỏ qua name/phone.
  const picked = matchRecipient(source, now.delivery_name, now.delivery_phone);
  return DELIVERY_SNAPSHOT_KEYS.some((k) => {
    if (picked && (k === 'delivery_name' || k === 'delivery_phone')) return false;
    return now[k] !== (source.snapshot[k] ?? '');
  });
}

/** Entry sổ → giá trị form giao nhận; `delivery_note` tự điền từ ghi chú địa chỉ + người nhận khác. */
export function entryToDeliveryValues(e: AddressBookEntry): Record<string, unknown> {
  const city = entryCity(e);
  const ward = entryWard(e);
  const values: Record<string, unknown> = {
    delivery_same_as_customer: false,
    delivery_name: e.name ?? '',
    delivery_phone: e.phone ?? '',
    delivery_email: e.email ?? '',
    delivery_citizen_id: e.citizen_id ?? '',
    delivery_city_id: readAddressLocationId(city) || undefined,
    _deliveryCityName: readAddressLocationName(city),
    delivery_ward_id: readAddressLocationId(ward) || undefined,
    _deliveryWardName: readAddressLocationName(ward),
    delivery_address: e.address ?? '',
    delivery_save_to_book: false,
    delivery_add_receiver_to_book: false,
    delivery_note: buildDeliveryNote(e.note, entryRecipients(e), e.phone ?? ''),
    _deliveryNoteTouched: false,
  };
  values._deliveryAddressSource = {
    address_id: e.address_id,
    snapshot: takeDeliverySnapshot(values),
    recipients: entryRecipients(e),
    note: e.note,
    address_name: e.address_name,
  } satisfies DeliveryAddressSource;
  return values;
}

/**
 * Body ghi sổ từ form giao nhận. `null` khi thiếu field BE bắt buộc (name/city/ward/address) —
 * gọi API chắc chắn 400. `is_default` CHỈ gửi khi `true`: `updateAddress` ở BE `Object.assign`
 * nguyên body, gửi `false` cho entry đang mặc định là xoá mất địa chỉ mặc định của khách.
 */
export function buildAddressBody(
  values: Record<string, unknown>,
  opts: { isDefault?: boolean } = {},
): Record<string, unknown> | null {
  const s = takeDeliverySnapshot(values);
  if (!s.delivery_name || !s.delivery_city_id || !s.delivery_ward_id || !s.delivery_address) {
    return null;
  }
  // Đang chọn người nhận thêm: KHÔNG ghi đè người chính của địa chỉ bằng người đó.
  const source = values._deliveryAddressSource as DeliveryAddressSource | undefined;
  const picked = matchRecipient(source, s.delivery_name, s.delivery_phone);
  const keepMain = picked && !picked.is_main && source ? source.snapshot : undefined;
  const name = keepMain?.delivery_name || s.delivery_name;
  const phone = keepMain ? keepMain.delivery_phone : s.delivery_phone;
  const body: Record<string, unknown> = {
    name,
    city_id: s.delivery_city_id,
    ward_id: s.delivery_ward_id,
    address: s.delivery_address,
  };
  if (phone) body.phone = phone;
  if (s.delivery_email) body.email = s.delivery_email;
  // CCCD đang bị che (chưa giải mã) thì KHÔNG gửi — ghi chuỗi che vào sổ là mất CCCD thật.
  if (s.delivery_citizen_id && !isMaskedCccd(s.delivery_citizen_id)) {
    body.citizen_id = s.delivery_citizen_id;
  }
  if (opts.isDefault) body.is_default = true;
  return body;
}
